<template>
  <b-sidebar
    id="help-panel"
    v-model="panelOpen"
    right
    shadow
    backdrop
    no-header-close
  >
    <template #header>
      <div class="d-flex align-items-center w-100">
        <strong class="mr-auto">Help</strong>
        <button v-if="visibleEntry && !editing" class="help-header-btn" @click="popOut" title="Open in new window">
          <font-awesome-icon icon="external-link-alt" />
        </button>
        <button v-if="userIsAdmin && !editing" class="help-edit-btn" @click="startEdit" title="Edit help content">
          <font-awesome-icon icon="pencil-alt" />
        </button>
      </div>
    </template>
    <div class="p-3">
      <!-- Loading state -->
      <div v-if="entry === undefined" class="text-muted">Loading...</div>

      <div v-else>
        <!-- No content -->
        <div v-if="visibleEntry === null && !editing" class="text-muted">No help available for this page.</div>

        <!-- Content (read mode) -->
        <div v-if="visibleEntry !== null && !editing">
          <div v-if="userIsAdmin && !entry.published" class="draft-badge">Draft</div>
          <h5>{{ entry.title }}</h5>
          <div v-html="renderedBody" class="help-body"></div>
        </div>

        <!-- Edit mode (admin only) -->
        <div v-if="editing">
          <b-form-input v-model="editTitle" class="mb-2" placeholder="Title" />
          <b-form-textarea v-model="editBody" rows="10" class="mb-2" placeholder="Write help content in Markdown..." />
          <b-form-checkbox v-model="editPublished" class="mb-2">Published</b-form-checkbox>
          <b-button variant="primary" size="sm" @click="save">Save</b-button>
          <b-button variant="secondary" size="sm" class="ml-2" @click="cancelEdit">Cancel</b-button>
        </div>

      </div>
    </div>

    <template #footer>
      <b-button variant="secondary" size="sm" class="m-3" @click="close">Close</b-button>
    </template>
  </b-sidebar>
</template>

<script>
import {markdownToHtml} from '@/utils/markdownToHtml';

export default {
  name: 'HelpPanel',
  data() {
    return {
      editing: false,
      editTitle: '',
      editBody: '',
      editPublished: false
    };
  },
  computed: {
    panelOpen: {
      get() { return this.$store.state.help.panelOpen; },
      set(val) { if (!val) this.$store.dispatch('help/closePanel'); }
    },
    currentKey() { return this.$store.state.help.currentKey; },
    entry() { return this.$store.getters['help/getByKey'](this.currentKey); },
    renderedBody() {
      if (!this.entry) return '';
      return markdownToHtml(this.entry.bodyMd);
    },
    userIsAdmin() {
      var roles = this.$store.state.user.roles;
      if (!roles) return false;
      var list = Array.isArray(roles) ? roles : String(roles).split(',');
      return list.some(function (r) {
        return r.trim() === 'admin';
      });
    },
    // Non-admins only see published content; admins see everything
    visibleEntry() {
      if (this.entry === undefined || this.entry === null) return this.entry;
      if (!this.entry.published && !this.userIsAdmin) return null;
      return this.entry;
    }
  },
  watch: {
    currentKey() {
      this.editing = false;
      this.editTitle = '';
      this.editBody = '';
      this.editPublished = false;
    }
  },
  methods: {
    close() { this.$store.dispatch('help/closePanel'); },
    startEdit() {
      this.editTitle = (this.entry && this.entry.title) ? this.entry.title : '';
      this.editBody = (this.entry && this.entry.bodyMd) ? this.entry.bodyMd : '';
      this.editPublished = !!(this.entry && this.entry.published);
      this.editing = true;
    },
    cancelEdit() { this.editing = false; },
    async save() {
      try {
        var base = this.entry ? Object.assign({}, this.entry) : { scope: 'page' };
        await this.$store.dispatch('help/saveHelp', {
          key: this.currentKey,
          content: Object.assign(base, {title: this.editTitle, bodyMd: this.editBody, published: this.editPublished})
        });
        this.editing = false;
      } catch (e) {
        this.$toasted.error('Failed to save help content.');
      }
    },
    popOut() {
      if (!this.currentKey) return;
      var resolved = this.$router.resolve({ name: 'HelpPopup', params: { key: this.currentKey } });
      window.open(resolved.href, '_blank', 'width=680,height=700');
    }
  }
}
</script>

<style scoped>
.help-header-btn,
.help-edit-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #6c757d;
  padding: 4px 8px;
  font-size: 0.86rem;
}
.help-header-btn:hover,
.help-edit-btn:hover {
  color: #343a40;
}

.draft-badge {
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  color: #856404;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 3px;
  padding: 1px 6px;
  margin-bottom: 6px;
}
</style>
